import type {EntranceKind} from './useEntranceKind';

const CURTAIN_EASE = 'cubic-bezier(.7,0,.2,1)';
const SETTLE_EASE = 'cubic-bezier(.22,1,.36,1)';
const FADE_EASE = 'cubic-bezier(.4,0,.2,1)';
const FLIGHT_MS = 900;

export const ENTRANCE_EXIT_MS: Record<EntranceKind, number> = {
  property: 1450,
  reservation: FLIGHT_MS + 120,
  instant: 1450,
};

const isPhotoFullScreen = (header: HTMLElement) =>
  (header.firstElementChild?.getBoundingClientRect().height ?? 0) >=
  document.documentElement.clientHeight;

const find = (selector: string, scope: ParentNode = document) =>
  scope.querySelector<HTMLElement>(selector);

function fadeContent(root: HTMLElement) {
  const content = find('[data-entrance="content"]', root);
  content?.animate([{opacity: 1}, {opacity: 0}], {
    duration: 280,
    easing: 'ease',
    fill: 'forwards',
  });
  content?.animate([{transform: 'none'}, {transform: 'translateY(-18px) scale(.98)'}], {
    duration: 500,
    easing: FADE_EASE,
    fill: 'forwards',
  });
  find('[data-entrance="progress"]', root)?.animate([{opacity: 1}, {opacity: 0}], {
    duration: 200,
    fill: 'forwards',
  });
}

function shrinkPhotoToHeader(
  backdrop: HTMLElement,
  headerHeight: number,
  squash: number,
  timing: KeyframeAnimationOptions,
) {
  const photo = backdrop.querySelector<HTMLImageElement>('[data-entrance="photo"]');
  if (!photo?.naturalWidth) return;

  const {naturalWidth, naturalHeight, clientWidth, clientHeight} = photo;
  const cover = Math.max(clientWidth / naturalWidth, clientHeight / naturalHeight);
  const width = naturalWidth * cover;
  const height = naturalHeight * cover;
  const zoom = getComputedStyle(photo).transform;
  const start = zoom === 'none' ? '' : zoom;
  Object.assign(photo.style, {
    animation: 'none',
    transform: start,
    inset: 'auto',
    left: `${(clientWidth - width) / 2}px`,
    top: `${(clientHeight - height) / 2}px`,
    width: `${width}px`,
    maxWidth: 'none',
    height: `${height}px`,
    objectFit: 'fill',
  });

  const scale =
    Math.max(clientWidth / naturalWidth, headerHeight / naturalHeight) / cover;
  photo.animate(
    [
      {transform: start || 'none'},
      {transform: `${start} scale(${scale}, ${scale / squash})`},
    ],
    timing,
  );
}

function exitToPropertyLink(root: HTMLElement, backdrop: HTMLElement, isMorphed = false) {
  const header = find('[data-entrance="property-header"]');
  const card = find('[data-entrance="property-card"]');
  fadeContent(root);

  if (card && isMorphed) {
    card.animate([{opacity: 0}, {opacity: 0}], {duration: FLIGHT_MS});
  }

  if (!header || !card || isPhotoFullScreen(header)) {
    backdrop.animate([{opacity: 1}, {opacity: 0}], {
      duration: 700,
      delay: 200,
      easing: FADE_EASE,
      fill: 'forwards',
    });
    if (isMorphed) return;
    card?.animate(
      [
        {opacity: 0, transform: 'translateY(28px)'},
        {opacity: 1, transform: 'none'},
      ],
      {
        duration: 900,
        delay: 300,
        easing: SETTLE_EASE,
        fill: 'backwards',
      },
    );
    return;
  }

  const rise = Math.max(0, window.innerHeight - card.getBoundingClientRect().top);
  const timing = {
    duration: 900,
    delay: 150,
    easing: CURTAIN_EASE,
    fill: 'forwards',
  } as const;
  const squash = header.offsetHeight / window.innerHeight;
  backdrop.animate([{transform: 'none'}, {transform: `translateY(${-rise}px)`}], timing);
  backdrop.firstElementChild?.animate(
    [
      {transformOrigin: '0 0', transform: 'none'},
      {transformOrigin: '0 0', transform: `translateY(${rise}px) scaleY(${squash})`},
    ],
    timing,
  );
  shrinkPhotoToHeader(backdrop, header.offsetHeight, squash, timing);
  backdrop.animate([{opacity: 1}, {opacity: 0}], {
    duration: 400,
    delay: 1020,
    easing: 'ease',
    fill: 'forwards',
  });
  if (isMorphed) return;
  card.animate([{transform: `translateY(${rise}px)`}, {transform: 'none'}], {
    ...timing,
    fill: 'backwards',
  });
}

function exitToHome(root: HTMLElement, backdrop: HTMLElement) {
  const glass = find('[data-entrance="card"]', root);
  const hero = find('[data-entrance="hero"]');

  backdrop.animate([{opacity: 1}, {opacity: 0}], {
    duration: 520,
    delay: 260,
    easing: FADE_EASE,
    fill: 'forwards',
  });

  if (!glass || !hero) {
    fadeContent(root);
    return;
  }

  glass.getAnimations().forEach(animation => animation.finish());
  const from = glass.getBoundingClientRect();
  const to = hero.getBoundingClientRect();
  const scale = from.width / to.width;
  const dx = from.left - to.left;
  const dy = from.top + (from.height - to.height * scale) / 2 - to.top;

  hero.animate(
    [
      {
        transformOrigin: '0 0',
        transform: `translate(${dx}px,${dy}px) scale(${scale})`,
        opacity: 0,
      },
      {opacity: 0, offset: 0.14},
      {opacity: 1, offset: 0.5},
      {transformOrigin: '0 0', transform: 'none', opacity: 1},
    ],
    {duration: FLIGHT_MS, easing: CURTAIN_EASE},
  );

  const back = 1 / scale;
  const tx = to.left - from.left;
  const ty = to.top + (to.height - from.height * back) / 2 - from.top;
  glass.animate(
    [
      {transform: 'none', opacity: 1},
      {opacity: 1, offset: 0.08},
      {opacity: 0, offset: 0.32},
      {transform: `translate(${tx}px,${ty}px) scale(${back})`, opacity: 0},
    ],
    {duration: FLIGHT_MS, easing: CURTAIN_EASE, fill: 'forwards'},
  );
  Array.from(hero.parentElement?.children ?? [])
    .filter(sibling => sibling !== hero)
    .forEach((sibling, index) =>
      sibling.animate(
        [
          {opacity: 0, transform: 'translateY(18px)'},
          {opacity: 1, transform: 'none'},
        ],
        {
          duration: 560,
          delay: 380 + Math.min(index, 4) * 60,
          easing: SETTLE_EASE,
          fill: 'backwards',
        },
      ),
    );
}

function morphIntoCard(root: HTMLElement): boolean {
  const glass = find('[data-entrance="card"]', root);
  const section = find('[data-entrance="property-card"] section');
  const card =
    section && parseFloat(getComputedStyle(section).borderTopLeftRadius)
      ? section
      : section?.parentElement;
  if (!glass || !card) return false;

  glass.getAnimations().forEach(animation => animation.finish());
  const from = glass.getBoundingClientRect();
  const to = card.getBoundingClientRect();
  const height = Math.min(to.height, window.innerHeight - to.top + 40);

  const sheet = document.createElement('i');
  sheet.setAttribute('aria-hidden', 'true');
  Object.assign(sheet.style, {
    position: 'fixed',
    zIndex: '3',
    top: `${to.top}px`,
    left: `${to.left}px`,
    width: `${to.width}px`,
    height: `${height}px`,
    borderRadius: getComputedStyle(card).borderTopLeftRadius,
    background: 'var(--chekin-color-white)',
    transformOrigin: '0 0',
    pointerEvents: 'none',
  });
  root.appendChild(sheet);

  const dx = from.left - to.left;
  const dy = from.top - to.top;
  sheet.animate(
    [
      {
        transform: `translate(${dx}px,${dy}px) scale(${from.width / to.width},${
          from.height / height
        })`,
        opacity: 0,
      },
      {opacity: 1, offset: 0.25},
      {transform: 'none', opacity: 1},
    ],
    {duration: FLIGHT_MS, easing: CURTAIN_EASE, fill: 'forwards'},
  );
  sheet.animate([{opacity: 1}, {opacity: 0}], {
    duration: 260,
    delay: FLIGHT_MS,
    easing: 'ease-out',
    fill: 'forwards',
  });
  return true;
}

function playEntranceExit(kind: EntranceKind, root: HTMLElement) {
  const backdrop = find('[data-entrance="backdrop"]', root);
  if (!backdrop) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    root.animate([{opacity: 1}, {opacity: 0}], {duration: 300, fill: 'forwards'});
    return;
  }

  if (kind === 'property') {
    exitToPropertyLink(root, backdrop);
    return;
  }

  if (kind === 'instant') {
    exitToPropertyLink(root, backdrop, morphIntoCard(root));
    return;
  }

  exitToHome(root, backdrop);
}

export {playEntranceExit};
